import { TExamListItemDto } from "@/api/services/main/learning-progress/dtos/exam/get-exam-list-by-industry.dto";
import { useLearningProgressExamDetail } from "@/api/services/main/learning-progress";
import { getDefaultExamId } from "@/features/learning-progress/utils/exam-schedule";
import { Select } from "@/components/elements/Select";
import { ExamLinksTab } from "@/features/learning-progress/components/exam/ExamLinksTab";
import { ExamOverviewTab } from "@/features/learning-progress/components/exam/ExamOverviewTab";
import { ExamScheduleTab } from "@/features/learning-progress/components/exam/ExamScheduleTab";
import { ExamSummaryTabs } from "@/features/learning-progress/components/exam/ExamSummaryTabs";
import { Stack, Typography } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { LoadingArea } from "@/components/elements/Loading/Loading";
import { EXAM_STATUS } from "@/types/enum";

type TExamSummarySectionProps = {
  exams: TExamListItemDto[];
  industryId: number;
};

type TTab = "overview" | "schedule" | "links";
const TABS: { value: TTab; label: string }[] = [
  { value: "overview", label: "試験概要" },
  { value: "schedule", label: "スケジュール" },
  { value: "links", label: "関連リンク" },
];

export const ExamSummarySection: FC<TExamSummarySectionProps> = ({ exams, industryId }) => {
  // 終了済み・無効な試験はサマリーUIに出さない（過去試験はフィルタ側のみで使う）。
  const activeExams = useMemo(() => exams.filter((e) => e.status === EXAM_STATUS.ACTIVE), [exams]);

  const [selectedExamId, setSelectedExamId] = useState<number | null>(getDefaultExamId(activeExams));
  const [activeTab, setActiveTab] = useState<TTab>("overview");

  useEffect(() => {
    setSelectedExamId((prev) => (prev !== null && activeExams.some((e) => e.id === prev) ? prev : getDefaultExamId(activeExams)));
  }, [activeExams]);

  const { data: resDto, isLoading } = useLearningProgressExamDetail({
    industryId,
    examId: selectedExamId,
  });
  const selectedExamDetail = resDto?.data ?? null;

  const examOptions = useMemo(
    () =>
      activeExams.map((exam) => ({
        value: String(exam.id),
        label: exam.periodName,
      })),
    [activeExams],
  );

  return (
    <Stack gap={2}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
        <Typography variant="head20Bold">{TABS.find((t) => t.value === activeTab)?.label}</Typography>
        <Stack direction="row" alignItems="center" gap={2}>
          <Select
            name="exam-select"
            nonControl
            options={examOptions}
            value={selectedExamId !== null ? String(selectedExamId) : ""}
            onChange={(val) => setSelectedExamId(Number(val))}
            clearable={false}
            sx={{ minWidth: 280 }}
          />
          <ExamSummaryTabs value={activeTab} onChange={(val) => setActiveTab(val)} options={TABS} />
        </Stack>
      </Stack>

      {isLoading ? (
        <LoadingArea height={120} />
      ) : (
        <>
          {/* Content */}
          {activeTab === "overview" && <ExamOverviewTab exam={selectedExamDetail} />}
          {activeTab === "schedule" && <ExamScheduleTab exam={selectedExamDetail} />}
          {activeTab === "links" && <ExamLinksTab exam={selectedExamDetail} />}
        </>
      )}
    </Stack>
  );
};
